'use client'

import { useEffect } from 'react';
import { useRouter } from 'next/navigation'
import { useAuth } from '@/contexts/auth-context'

import { logger } from '@/utils/logger'
import { EMPTY_GUID } from '@/lib/validation'

export function FetchProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter()
  const { user, logout } = useAuth()

  useEffect(() => {
    const originalFetch = window.fetch

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      try {
        // Check for empty GUID requests and block them
        const url = typeof input === 'string' 
          ? input 
          : input instanceof Request 
            ? input.url 
            : input.toString();
            
        if (url.includes(EMPTY_GUID)) {
          logger.warn('Blocked request to empty GUID endpoint', 'FetchProvider', { url });
          return Promise.resolve(new Response('{"error": "Empty GUID not allowed"}', { 
            status: 400, 
            statusText: 'Bad Request',
            headers: { 'Content-Type': 'application/json' }
          }));
        }

        const response = await originalFetch(input, init)

        // Handle 401 Unauthorized responses
        if (response.status === 401) {
          const url = typeof input === 'string'
            ? input
            : input instanceof Request
              ? input.url
              : input.toString();
          logger.info('Unauthorized response received', 'FetchProvider', { url })

          // Skip auth handling for auth-related endpoints to prevent loops
          if (url.includes('/api/auth/')) {
            logger.info('Skipping auth handling for auth endpoint', 'FetchProvider', { url });
            return response
          }

          // Skip auth handling for entity endpoints - they handle 401s gracefully
          // This prevents logout when user lacks permission for specific entities
          if (url.includes('/api/entities/')) {
            logger.info('Skipping auth handling for entity endpoint - may lack entity permissions', 'FetchProvider', { url });
            return response
          }

          // If we're on the callback page or login page, don't redirect
          const currentPath = window.location.pathname;
          if (currentPath === '/login' || currentPath === '/auth/callback') {
            logger.info('Skipping auth handling on auth page', 'FetchProvider', {
              currentPath,
              requestUrl: url
            });
            return response
          }

          // Only redirect if user was authenticated - prevents infinite loops
          if (user) {
            logger.info('Authenticated user received 401, logging out and redirecting', 'FetchProvider');
            await logout();
          } else {
            logger.info('Unauthenticated user received 401, redirecting to login', 'FetchProvider');
            router.push('/login');
          }

          return response
        }

        return response
      } catch (_error) {
        // Don't log AbortErrors - they're expected when requests are cancelled
        if (_error instanceof Error && _error.name !== 'AbortError') {
          logger.error('Fetch error', 'FetchProvider', _error)
        }
        throw _error
      }
    }

    return () => {
      window.fetch = originalFetch
    }
  }, [router, logout, user])

  return <>{children}</>
}